/**
 * Quotes API Service
 * Handles quote-related API calls
 */

import { ApiClient } from '../core/api-client';
import { isApiError } from '../core/errors';
import { logger } from '../../utils/logger';

export interface Quote {
  id: string;
  text: string;
  author?: string;
  source?: string;
  category?: string;
  date?: string;
  language?: string;
}

export interface QuoteResponse {
  data: Quote | null;
  metadata?: {
    date: string;
    language: string;
  };
}

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8080/api';

export class QuoteService {
  private client: ApiClient;

  constructor() {
    this.client = ApiClient.getInstance(API_BASE_URL);
  }

  /**
   * Get quote of the day
   * Returns null if quote not found (404) or user not authenticated
   */
  async getQuoteOfTheDay(language: string = 'en'): Promise<Quote | null> {
    try {
      const response = await this.client.get<QuoteResponse>(`/quote/today`, {
        credentials: 'include',
      });

      return response.data?.data || null;
    } catch (error) {
      if (isApiError(error)) {
        // 401/403 Unauthorized or 404 Not Found - gracefully return null
        if (error.statusCode === 401 || error.statusCode === 403 || error.statusCode === 404) {
          if (error.statusCode === 404) {
            console.debug('[QuoteService] Quote not found for today');
          }
          return null;
        }

        // Other errors should be logged but not thrown for non-critical feature
        console.debug('[QuoteService] Failed to fetch quote of the day:', error.message);
        return null;
      }

      console.debug('[QuoteService] Failed to fetch quote of the day', error);
      return null;
    }
  }

  /**
   * Get quote by ID
   */
  async getQuoteById(quoteId: string): Promise<Quote | null> {
    try {
      const response = await this.client.get<QuoteResponse>(`/quote/${quoteId}`, {
        credentials: 'include',
      });

      return response.data?.data || null;
    } catch (error) {
      if (isApiError(error) && error.statusCode === 404) {
        logger.warn(`Quote ${quoteId} not found`);
        return null;
      }

      logger.error('Failed to fetch quote', error);
      return null;
    }
  }

  /**
   * Search quotes
   */
  async searchQuotes(
    query: string,
    options?: {
      limit?: number;
      offset?: number;
      category?: string;
      author?: string;
    }
  ): Promise<Quote[]> {
    try {
      const params = new URLSearchParams();
      params.append('q', query);
      if (options?.limit) params.append('limit', options.limit.toString());
      if (options?.offset) params.append('offset', options.offset.toString());
      if (options?.category) params.append('category', options.category);
      if (options?.author) params.append('author', options.author);

      const url = `/quote/search?${params.toString()}`;
      const response = await this.client.get<{ data: Quote[] }>(url, {
        credentials: 'include',
      });

      return response.data?.data || [];
    } catch (error) {
      logger.error('Failed to search quotes', error);
      return [];
    }
  }

  /**
   * Get random quote
   */
  async getRandomQuote(): Promise<Quote | null> {
    try {
      const response = await this.client.get<QuoteResponse>(`/quote/random`, {
        credentials: 'include',
      });

      return response.data?.data || null;
    } catch (error) {
      if (isApiError(error) && error.statusCode === 404) {
        logger.warn('No quotes available');
        return null;
      }

      logger.error('Failed to fetch random quote', error);
      return null;
    }
  }
}

// Export singleton instance
export const quoteService = new QuoteService();
